import { CircleCheck, CircleX } from "lucide-react";
import { Button } from "@/components/atoms/button";

interface InvitationStatusMessageProps {
  type: "success" | "error";
  email?: string;
  errorMessage?: string;
  onClose: () => void;
  mode?:
    | "invitation"
    | "deactivation"
    | "reactivation"
    | "permissions_update"
    | "password_change";
}

export default function InvitationStatusMessage({
  type,
  email,
  errorMessage,
  onClose,
  mode = "invitation",
}: InvitationStatusMessageProps) {
  const isSuccess = type === "success";

  // Get messages based on mode
  const getMessages = () => {
    if (mode === "deactivation") {
      return {
        success: {
          title: "Usuario desactivado exitosamente",
          subtitle: `Usuario "${email}" desactivado exitosamente.`,
        },
        error: {
          title: "Error al desactivar usuario",
          subtitle:
            "No se pudo desactivar el usuario. Inténtalo de nuevo más tarde.",
        },
      };
    }

    if (mode === "reactivation") {
      return {
        success: {
          title: "Usuario reactivado exitosamente",
          subtitle: `Usuario "${email}" reactivado exitosamente.`,
        },
        error: {
          title: "Error al reactivar usuario",
          subtitle:
            "No se pudo reactivar el usuario. Inténtalo de nuevo más tarde.",
        },
      };
    }

    if (mode === "permissions_update") {
      return {
        success: {
          title: "Permisos actualizados correctamente",
          subtitle: `Los permisos del usuario "${email}" han sido actualizados exitosamente.`,
        },
        error: {
          title: "Error al actualizar permisos",
          subtitle:
            "No se pudieron actualizar los permisos. Inténtalo de nuevo más tarde.",
        },
      };
    }

    if (mode === "password_change") {
      return {
        success: {
          title: "Email enviado exitosamente",
          subtitle:
            "Revisa tu bandeja de entrada y carpeta de SPAM, y sigue las instrucciones para cambiar tu contraseña. Si no encuentras el email, revisa la carpeta de correo no deseado.",
        },
        error: {
          title: "Error al enviar email",
          subtitle:
            "No se pudo enviar el email de cambio de contraseña. Contacte a info@iungo.co para obtener ayuda.",
        },
      };
    }

    // Default invitation mode
    return {
      success: {
        title: "Invitación enviada a",
        subtitle: email || "",
      },
      error: {
        title: "Algo salió mal",
        subtitle: "Inténtalo de nuevo más tarde",
      },
    };
  };

  const messages = getMessages();

  return (
    <div className="flex flex-col items-center justify-center p-6 relative animate-in fade-in-0 zoom-in-95 duration-200">
      <div className="mb-4">
        {/* Outer circle with light background */}
        <div
          className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${
            isSuccess ? "bg-green-100" : "bg-red-100"
          }`}
        >
          {/* Inner circle with solid background */}
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-full"
            style={{ backgroundColor: isSuccess ? "#10B978" : "#FF4D4D" }}
          >
            {isSuccess ? (
              <CircleCheck className="w-8 h-8 text-white" />
            ) : (
              <CircleX className="w-8 h-8 text-white" />
            )}
          </div>
        </div>
      </div>

      {isSuccess ? (
        <>
          <div className="text-lg font-medium mb-2 text-center">
            {messages.success.title}
          </div>
          {mode === "invitation" ? (
            <div className="text-center font-semibold text-black mb-6">
              {messages.success.subtitle}
            </div>
          ) : (
            <div className="text-center text-gray-600 mb-6">
              {messages.success.subtitle}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="text-lg font-bold mb-2 text-center">
            {messages.error.title}
          </div>
          <div className="text-center text-gray-600 mb-4">
            {messages.error.subtitle}
          </div>
          {errorMessage && (
            <div className="text-center text-sm text-gray-500 mb-6 px-4 py-2 bg-red-50 rounded-lg border border-red-200">
              <span className="font-medium">Detalle del error:</span>
              <br />
              {errorMessage == "Failed to fetch"
                ? "No se pudo realizar la solicitud al servidor. Inténtelo de nuevo más tarde."
                : errorMessage}
            </div>
          )}
          {!errorMessage && <div className="mb-6"></div>}
        </>
      )}

      <Button
        className="w-full bg-[#2201CE] hover:bg-[#1A019E] text-white"
        onClick={onClose}
      >
        Cerrar
      </Button>
    </div>
  );
}
